/**
 * S12b CADCORE-GRIPS-SELECT-WIRE — live typed-entity resolver for the grip
 * source.
 *
 * Deliberately SEPARATE from the pure `typedEntityGrips.ts` mapper: this is
 * the ONLY grip module that reaches into the editor store (`getEntityById` /
 * `getEntityFromCompactCache`). Keeping it isolated lets `gripsOverlay.tsx`
 * and the pure mapper stay store-free (they receive the resolver by
 * injection), so their unit tests don't pull the heavy store module graph in.
 * `Scene3DStage` — which already imports the store — wires this in as the
 * live `resolveTypedEntity` prop.
 */

import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { getEntityById } from "../../state/editorStore";
import { getEntityFromCompactCache } from "../../state/mutationStore";

/**
 * Resolve the live typed `DrawingEntity` for a rendered entity id, using the
 * SAME Map → compact-cache ordering + Attdef narrowing as the typed-first
 * render resolver (`scene/SceneContentRefs.ts::resolveEntity`, DEFUSE S11) —
 * `getEntityById` already encapsulates the freshness ordering (editorStore
 * Iter 6.A). Returns `null` when the id resolves to nothing renderable
 * (missing, or an AttDef, which carries no on-canvas grips).
 */
export function resolveTypedEntityById(id: string): DrawingEntity | null {
  const entity = getEntityById(id) ?? getEntityFromCompactCache(id);
  if (!entity || entity.type === "Attdef") return null;
  return entity;
}
